/**
 * Ben's first-run tour script. Declarative — each step is a JSON-shaped
 * object the `FirstRunTour` overlay reads + executes. Voice copy is
 * piped to `useTextToSpeech` (ElevenLabs George by default for review
 * coaches); UI targeting via DOM selectors that already exist on the
 * destination routes.
 *
 * Keep the tour SHORT — coaches are non-tech and patience is finite.
 * 60-90 seconds total including pauses.
 */

export interface FirstRunStep {
  id: string;
  /** What Brain says aloud + shows as the chat bubble. `{coachName}` is
   *  substituted with the coach's first name. */
  brainSay: string;
  /** Optional target route to navigate to before speaking. The
   *  spotlight waits for the selector to mount on this route. */
  target?: {
    route?: string;
    /** CSS selector for the element to spotlight. Optional — if
     *  omitted, the step has no spotlight (welcome / final step). */
    selector?: string;
    /** Optional action to fire when entering the step (e.g. auto-play
     *  the drill animation on Step 3). */
    action?: 'autoplay-drill';
  };
  /** Voice replies that auto-advance the tour. Coach can also tap
   *  Next at any time. Matched case-insensitively, substring match. */
  expectedReplies?: string[];
  /** Final step uses the voice routing classifier instead of advancing. */
  isFinal?: boolean;
}

export const FIRST_RUN_STEPS: FirstRunStep[] = [
  {
    id: 'welcome',
    brainSay: "Hi {coachName}, I'm Brain. I'm your assistant — you'll talk to me by voice. I've already set up your squad and a few sessions for the week. Say 'ready' or tap Next when you're set.",
    expectedReplies: ['ready', 'go', 'yes', 'next', "let's go", "let's start"],
  },
  {
    id: 'sessions',
    brainSay: "Your sessions are over here. I've drafted three for the next seven days — a possession warm-up, a pressing block, and a finishing pattern. Tap one to see the full plan.",
    target: { route: '/sessions', selector: '[data-nav-item="sessions"]' },
    expectedReplies: ['next', 'ok', 'got it', 'continue', 'go on'],
  },
  {
    id: 'drill',
    brainSay: "These are the drills in your library — including a 4v1 diamond rondo. Each one has a diagram you can edit in 2D and flip to 3D to see from any angle. Tap a card any time to open it up.",
    // Spotlight the first card in the drills library — keeps Step 3
    // robust even though seed-generated IDs aren't known here.
    target: { route: '/drills', selector: '.drills-list li:first-child' },
    expectedReplies: ['next', 'ok', 'got it', 'continue'],
  },
  {
    id: 'voice-feedback',
    brainSay: "Quick thing — see the green pill at the top? Voice feedback is on. As you use the app, anything you say like \"I don't like this button\" gets flagged for Peter. So just talk to me normally and tell me what you think.",
    target: { selector: '[data-testid="coach-review-banner"]' },
    expectedReplies: ['next', 'ok', 'understood', 'got it', 'continue'],
  },
  {
    id: 'open',
    brainSay: "That's the tour. What would you like to start with — your sessions, or the drill library?",
    isFinal: true,
  },
];

export const FIRST_RUN_INTENT_PROMPT = `
Classify the coach's reply into one route:
- "sessions" — the coach wants to see their planned sessions for the week
- "drills" — the coach wants to browse or build drills
- "open" — the coach hasn't picked anything specific, or wants something else

Return ONLY JSON: { "route": "sessions" | "drills" | "open" }
`;

export interface IntentResult {
  route: 'sessions' | 'drills' | 'open';
}

export const INTENT_TO_PATH: Record<IntentResult['route'], string> = {
  sessions: '/sessions',
  drills: '/drills',
  open: '/sessions',
};

/** Substitute the coach's first name into a step's brainSay template. */
export function fillSayCopy(template: string, coachName: string | null): string {
  const first = (coachName ?? 'Coach').split(/\s+/)[0];
  return template.replace(/\{coachName\}/g, first);
}
